import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import { Button } from '../../components/ui/button';
import PageHeader from '../../components/common/PageHeader';
import { Download } from 'lucide-react';

type Trade = {
  ticket: string;
  master: string;
  follower: string;
  symbol: string;
  volume: number;
  profit: number;
  openTime: string;
  closeTime: string;
  type: 'buy' | 'sell';
  service: 'copy_trading' | 'pamm' | 'mam';
};

const TradingTradeHistory = () => {
  const [serviceFilter, setServiceFilter] = useState<
    'all' | 'copy_trading' | 'pamm' | 'mam'
  >('all');

  const trades: Trade[] = [
    {
      ticket: 'TRD001',
      master: 'John Smith',
      follower: 'Jane Doe',
      symbol: 'EURUSD',
      volume: 1.5,
      profit: 450.5,
      openTime: '2024-01-15 10:30:00',
      closeTime: '2024-01-15 14:45:00',
      type: 'buy',
      service: 'copy_trading',
    },
    {
      ticket: 'TRD002',
      master: 'Mike Johnson',
      follower: 'Sarah Williams',
      symbol: 'GBPUSD',
      volume: 2.0,
      profit: -120.0,
      openTime: '2024-01-15 09:15:00',
      closeTime: '2024-01-15 11:30:00',
      type: 'sell',
      service: 'pamm',
    },
    {
      ticket: 'TRD003',
      master: 'David Brown',
      follower: 'Tom Davis',
      symbol: 'XAUUSD',
      volume: 0.5,
      profit: 890.0,
      openTime: '2024-01-15 08:00:00',
      closeTime: '2024-01-15 16:20:00',
      type: 'buy',
      service: 'mam',
    },
    {
      ticket: 'TRD004',
      master: 'John Smith',
      follower: 'Emily Chen',
      symbol: 'USDJPY',
      volume: 1.0,
      profit: 230.75,
      openTime: '2024-01-14 15:30:00',
      closeTime: '2024-01-14 18:45:00',
      type: 'sell',
      service: 'copy_trading',
    },
    {
      ticket: 'TRD005',
      master: 'Mike Johnson',
      follower: 'Robert Wilson',
      symbol: 'EURGBP',
      volume: 1.8,
      profit: -85.5,
      openTime: '2024-01-14 11:00:00',
      closeTime: '2024-01-14 13:15:00',
      type: 'buy',
      service: 'pamm',
    },
    {
      ticket: 'TRD006',
      master: 'David Brown',
      follower: 'Lisa Anderson',
      symbol: 'AUDUSD',
      volume: 2.5,
      profit: 560.25,
      openTime: '2024-01-14 07:30:00',
      closeTime: '2024-01-14 15:00:00',
      type: 'sell',
      service: 'mam',
    },
  ];

  const filteredTrades = trades.filter(
    trade => serviceFilter === 'all' || trade.service === serviceFilter
  );

  const tradeColumns: DataTableColumn<Trade>[] = [
    {
      id: 'ticket',
      header: 'Ticket',
      accessor: row => <span className="font-medium">{row.ticket}</span>,
      sortable: true,
      sortValue: row => row.ticket,
      width: '100px',
    },
    {
      id: 'master',
      header: 'Master',
      accessor: row => row.master,
      sortable: true,
      sortValue: row => row.master,
      width: '150px',
    },
    {
      id: 'follower',
      header: 'Follower',
      accessor: row => row.follower,
      sortable: true,
      sortValue: row => row.follower,
      width: '150px',
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => <span className="font-semibold">{row.symbol}</span>,
      sortable: true,
      sortValue: row => row.symbol,
      width: '100px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'buy'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.type.toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '80px',
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => row.volume.toFixed(2),
      sortable: true,
      sortValue: row => row.volume,
      width: '100px',
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <span
          className={`font-semibold ${
            row.profit >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {row.profit >= 0 ? '+' : ''}${row.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit,
      width: '120px',
    },
    {
      id: 'service',
      header: 'Service',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.service === 'copy_trading'
              ? 'bg-blue-100 text-blue-800'
              : row.service === 'pamm'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-orange-100 text-orange-800'
          }`}
        >
          {row.service.replace('_', ' ').toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.service,
      width: '120px',
    },
    {
      id: 'openTime',
      header: 'Open Time',
      accessor: row => new Date(row.openTime).toLocaleString(),
      sortable: true,
      sortValue: row => row.openTime,
      width: '180px',
    },
    {
      id: 'closeTime',
      header: 'Close Time',
      accessor: row => new Date(row.closeTime).toLocaleString(),
      sortable: true,
      sortValue: row => row.closeTime,
      width: '180px',
    },
  ];

  const totalProfit = filteredTrades.reduce(
    (sum, trade) => sum + trade.profit,
    0
  );
  const totalVolume = filteredTrades.reduce(
    (sum, trade) => sum + trade.volume,
    0
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trade History"
          description="Complete trade history across all trading services"
        />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={serviceFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('all')}
          >
            All Services
          </Button>
          <Button
            variant={serviceFilter === 'copy_trading' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('copy_trading')}
          >
            Copy Trading
          </Button>
          <Button
            variant={serviceFilter === 'pamm' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('pamm')}
          >
            PAMM
          </Button>
          <Button
            variant={serviceFilter === 'mam' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('mam')}
          >
            MAM
          </Button>
        </div>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Trades</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{filteredTrades.length}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {serviceFilter === 'all'
                ? 'All services'
                : serviceFilter.replace('_', ' ').toUpperCase()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total Profit</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-3xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              ${totalProfit.toFixed(2)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Cumulative profit/loss
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalVolume.toFixed(2)}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Total lots traded
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Trade History</CardTitle>
          <CardDescription>
            Detailed trade records across all trading services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={tradeColumns}
            data={filteredTrades}
            searchPlaceholder="Search trades..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default TradingTradeHistory;
